import type {
    CaptureExportFacets,
    CaptureExportFilters,
    CaptureExportRow,
    CaptureFacetOption,
    CapturePhoto,
} from './types';

export const CAPTURE_PAGE_SIZE = 48;
/** Debe coincidir con el tope de `p_page_size` en admin_list_captures_for_export. */
export const CAPTURE_EXPORT_FETCH_SIZE = 1000;
/** Tope de capturas de una exportación de datos (JSON + Excel). */
export const CAPTURE_DATA_MAX = 20_000;
/** Capturas por ZIP de fotos: mantiene cada ZIP manejable en memoria del navegador. */
export const CAPTURE_PHOTO_BLOCK_SIZE = 300;

export const DEFAULT_CAPTURE_FILTERS: CaptureExportFilters = {
    species: '',
    bait: '',
    baitType: '',
    technique: '',
    waterType: '',
    platform: '',
    visibility: 'all',
    minWeightKg: '',
    maxWeightKg: '',
    minLengthCm: '',
    maxLengthCm: '',
    minLikes: '',
    dateFrom: '',
    dateTo: '',
    center: '',
    radiusKm: '',
    reliableSpeciesOnly: false,
    withPhotosOnly: true,
    sort: 'recent',
};

/** Acepta coma decimal («1,5»). Devuelve null si está vacío o no es número. */
export function parseDecimal(value: string): number | null {
    const text = value.trim().replace(',', '.');
    if (!text) return null;
    const n = Number(text);
    return Number.isFinite(n) ? n : null;
}

export interface GeoPreset {
    label: string;
    center: string;
}

export const GEO_PRESETS: readonly GeoPreset[] = [
    { label: 'A Coruña', center: '43.3623, -8.4115' },
    { label: 'Alicante', center: '38.3452, -0.4810' },
    { label: 'Barcelona', center: '41.3851, 2.1734' },
    { label: 'Bilbao', center: '43.2630, -2.9350' },
    { label: 'Cádiz', center: '36.5271, -6.2886' },
    { label: 'Cartagena', center: '37.6257, -0.9966' },
    { label: 'Gijón', center: '43.5322, -5.6611' },
    { label: 'Las Palmas', center: '28.1235, -15.4363' },
    { label: 'Madrid', center: '40.4168, -3.7038' },
    { label: 'Málaga', center: '36.7213, -4.4214' },
    { label: 'Palma', center: '39.5696, 2.6502' },
    { label: 'Santa Cruz de Tenerife', center: '28.4636, -16.2518' },
    { label: 'Sevilla', center: '37.3891, -5.9845' },
    { label: 'Tarragona', center: '41.1189, 1.2445' },
    { label: 'Valencia', center: '39.4699, -0.3763' },
    { label: 'Vigo', center: '42.2406, -8.7207' },
    { label: 'Zaragoza', center: '41.6488, -0.8891' },
];

/** «39.47, -0.38» o «39,47 -0,38» → coordenadas. Null si no son válidas. */
export function parseCenter(value: string): { lat: number; lng: number } | null {
    const text = value.trim();
    if (!text) return null;
    // Con punto decimal la coma separa; sin él, la coma es el decimal y separa el espacio.
    const parts = text.includes('.') ? text.split(/[,;\s]+/) : text.split(/[;\s]+/);
    const numbers = parts
        .map((part) => parseDecimal(part.replace(/^,+|,+$/g, '')))
        .filter((n): n is number => n != null);
    if (numbers.length !== 2) return null;
    const [lat, lng] = numbers;
    if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    return { lat, lng };
}

export function hasGeoFilter(filters: CaptureExportFilters): boolean {
    const radius = parseDecimal(filters.radiusKm);
    return parseCenter(filters.center) != null && radius != null && radius > 0;
}

/** Objeto `p_filters` de `admin_list_captures_for_export`: solo claves con valor. */
export function toRpcFilters(filters: CaptureExportFilters): Record<string, string | number | boolean> {
    const out: Record<string, string | number | boolean> = {
        visibility: filters.visibility,
        reliableSpeciesOnly: filters.reliableSpeciesOnly,
        withPhotosOnly: filters.withPhotosOnly,
        sort: filters.sort === 'distance' && !hasGeoFilter(filters) ? 'recent' : filters.sort,
    };
    const texts = ['species', 'bait', 'baitType', 'technique', 'waterType', 'platform', 'dateFrom', 'dateTo'] as const;
    for (const key of texts) {
        const value = filters[key].trim();
        if (value) out[key] = value;
    }
    const numbers = ['minWeightKg', 'maxWeightKg', 'minLengthCm', 'maxLengthCm', 'minLikes'] as const;
    for (const key of numbers) {
        const value = parseDecimal(filters[key]);
        if (value != null) out[key] = key === 'minLikes' ? Math.max(0, Math.round(value)) : value;
    }
    const center = parseCenter(filters.center);
    const radius = parseDecimal(filters.radiusKm);
    if (center && radius != null && radius > 0) {
        out.centerLat = center.lat;
        out.centerLng = center.lng;
        out.radiusKm = radius;
    }
    return out;
}

function num(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}

function text(value: unknown): string | null {
    if (value == null) return null;
    const s = String(value).trim();
    return s ? s : null;
}

function normalizePhotos(value: unknown): CapturePhoto[] {
    const list = typeof value === 'string' ? (JSON.parse(value) as unknown) : value;
    if (!Array.isArray(list)) return [];
    return list
        .map((item) => {
            const raw = (item ?? {}) as Record<string, unknown>;
            return {
                mediaId: text(raw.mediaId),
                path: text(raw.path) ?? '',
                isMain: raw.isMain === true,
            };
        })
        .filter((photo) => photo.path);
}

export function normalizeCaptureRow(raw: Record<string, unknown>): CaptureExportRow {
    return {
        id: String(raw.capture_id),
        captureDate: text(raw.capture_date),
        species: text(raw.species),
        speciesCommonName: text(raw.species_common_name),
        userCommonName: text(raw.user_common_name),
        weightKg: num(raw.weight_kg),
        lengthCm: num(raw.length_cm),
        baitLure: text(raw.bait_lure),
        baitType: text(raw.bait_type),
        technique: text(raw.technique),
        waterType: text(raw.water_type),
        location: text(raw.location),
        latitude: num(raw.latitude),
        longitude: num(raw.longitude),
        distanceKm: num(raw.distance_km),
        isPublic: raw.is_public === true,
        likesCount: num(raw.likes_count) ?? 0,
        commentsCount: num(raw.comments_count) ?? 0,
        moonPhase: text(raw.moon_phase),
        moonAgeDays: num(raw.moon_age_days),
        moonIllumination: num(raw.moon_illumination),
        fishingPlatform: text(raw.fishing_platform),
        waterClarity: text(raw.water_clarity),
        tideState: text(raw.tide_state),
        waterTemperature: num(raw.water_temperature),
        airTemperature: num(raw.air_temperature),
        windSpeed: num(raw.wind_speed),
        weatherConditions: text(raw.weather_conditions),
        description: text(raw.description),
        speciesLabelUnreliable: raw.species_label_unreliable === true,
        authorUsername: text(raw.author_username),
        authorDisplayName: text(raw.author_display_name),
        photos: normalizePhotos(raw.photos),
    };
}

function normalizeFacetList(value: unknown): CaptureFacetOption[] {
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => {
            const raw = (item ?? {}) as Record<string, unknown>;
            return { value: text(raw.value) ?? '', count: num(raw.count) ?? 0 };
        })
        .filter((option) => option.value);
}

export function normalizeFacets(raw: unknown): CaptureExportFacets {
    const data = (Array.isArray(raw) ? raw[0] : raw) as Record<string, unknown> | null | undefined;
    const facets = (data?.admin_capture_export_facets ?? data ?? {}) as Record<string, unknown>;
    return {
        techniques: normalizeFacetList(facets.techniques),
        waterTypes: normalizeFacetList(facets.waterTypes),
        baitTypes: normalizeFacetList(facets.baitTypes),
    };
}

/** Nombre visible de la especie: común del catálogo, el que puso el usuario o el científico. */
export function speciesLabel(row: Pick<CaptureExportRow, 'speciesCommonName' | 'userCommonName' | 'species'>): string {
    const common = row.speciesCommonName?.split(',')[0]?.trim();
    return common || row.userCommonName || row.species || 'Especie sin identificar';
}
